// src/modules/app-views/leads/pages/Init/modals/ImportLeadsModal/index.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useCallback, useMemo, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { ImportOutlined } from '@ant-design/icons';
import { ConfigProvider } from 'antd';
import GenericModal from 'components/core/GenericModal';
import appService from 'services/appService';
import translateX from 'utils/translateX';
import useZustand from 'hooks/useZustand';
import { darkTheme, lightTheme } from 'configs/appEngine';
import leadsService from 'modules/app-views/leads/service';
import importLeadsStep1 from './Step1';
import importLeadsStep2 from './Step2';
import importLeadsStep3 from './Step3';
import buildLeadPayload from './helpers/buildLeadPayload';
import validateRow from './helpers/validateRow';

// *******************************************************************************
// THEME WRAPPER COMPONENT
// *******************************************************************************
const ThemeWrapper = ({ children }) => {
  const theme = useZustand('theme', 'currentTheme');
  const config = theme === 'dark' ? darkTheme : lightTheme;
  return <ConfigProvider theme={config}>{children}</ConfigProvider>;
};

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const ImportLeadsModal = ({ show, setShow, afterClose, onSuccess }) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const [parseResult, setParseResult] = useState(null);
  const [importProgress, setImportProgress] = useState(null);
  const teardownRef = useRef(null);

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleParsed = useCallback(result => {
    setParseResult(result);
  }, []);

  const hasParsedFile = useCallback(() => {
    if (!parseResult) {
      appService.message({ flag: 'w', text: 'please_select_a_file_first' });
      return false;
    }
    return true;
  }, [parseResult]);

  const handleFinish = useCallback(async ({ form, setLoading }) => {
    if (!parseResult) return false;

    const mapping = form.getFieldValue('mapping') || {};
    const conflictStrategy = form.getFieldValue('conflictStrategy') || 'ignore';

    // Build only the valid payloads (client-side filter)
    const validLeads = [];
    (parseResult.rows || []).forEach((row, idx) => {
      const payload = buildLeadPayload(row, mapping, idx + 2);
      if (validateRow(payload).valid) validLeads.push(payload);
    });

    if (validLeads.length === 0) {
      appService.message({ flag: 'e', text: 'no_valid_rows_to_import' });
      return false;
    }

    setLoading(true);
    setImportProgress({ total: validLeads.length, processed: 0, created: 0, updated: 0, skipped: 0, errors: 0, finished: false });

    try {
      const response = await leadsService.startBulkImport({ leads: validLeads, conflictStrategy });
      const jobId = response?.jobId;
      if (!jobId) throw new Error(response?.message || 'missing_job_id');

      // Subscribe to SSE progress; resolve when 'done' fires or reject on 'error'.
      await new Promise((resolve, reject) => {
        teardownRef.current = leadsService.subscribeToImportProgress(jobId, ({ type, data }) => {
          if (type === 'progress') {
            setImportProgress(prev => ({ ...prev, ...data }));
          } else if (type === 'done') {
            setImportProgress(prev => ({ ...prev, ...data, finished: true }));
            resolve(data);
          } else if (type === 'error') {
            reject(new Error(data?.message || 'import_error'));
          }
        });
      });

      appService.message({ flag: 's', text: 'import_completed' });
      onSuccess?.();
      return true;
    } catch (error) {
      const msg = error?.response?.data?.message || error?.message || 'import_error';
      appService.message({ flag: 'e', text: msg });
      setLoading(false);
      return false;
    } finally {
      teardownRef.current?.();
      teardownRef.current = null;
    }
  }, [parseResult, onSuccess]);

  const handleAfterClose = useCallback(() => {
    teardownRef.current?.();
    teardownRef.current = null;
    setParseResult(null);
    setImportProgress(null);
    afterClose?.();
  }, [afterClose]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────
  const steps = useMemo(() => {
    const s1 = importLeadsStep1({ onParsed: handleParsed, hasParsedFile });
    const s2 = importLeadsStep2({
      columnsMeta: parseResult?.columnsMeta || [],
      rows: parseResult?.rows || [],
      initialMapping: parseResult?.mapping,
    });
    const s3 = importLeadsStep3({
      rows: parseResult?.rows || [],
      mapping: parseResult?.mapping || {},
      importProgress,
    });
    return [s1, s2, s3];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parseResult, importProgress, handleParsed, hasParsedFile]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <GenericModal
      show={show}
      setShow={setShow}
      title={translateX('import_leads')}
      icon={<ImportOutlined />}
      steps={steps}
      onFinish={handleFinish}
      afterClose={handleAfterClose}
      width={720}
      maskClosable={false}
    />
  );
};

// *******************************************************************************
// SHOW MODAL FUNCTION (createRoot pattern)
// *******************************************************************************
const showImportLeadsModal = ({ onSuccess } = {}) => {
  const container = document.createElement('div');
  container.id = `import-leads-modal-${Date.now()}`;
  document.body.appendChild(container);
  const root = createRoot(container);

  let isOpen = true;
  const renderModal = () => {
    root.render(
      <ThemeWrapper>
        <ImportLeadsModal
          show={isOpen}
          setShow={handleClose}
          afterClose={handleAfterClose}
          onSuccess={onSuccess}
        />
      </ThemeWrapper>
    );
  };

  const handleClose = () => {
    isOpen = false;
    renderModal();
  };

  const handleAfterClose = () => {
    setTimeout(() => {
      root.unmount();
      if (document.body.contains(container)) {
        document.body.removeChild(container);
      }
    }, 0);
  };

  renderModal();
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default ImportLeadsModal;
export { showImportLeadsModal };
